import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldCheck, Gavel, ScrollText, Workflow, KeyRound, Grid3x3, Activity as ActivityIcon, Loader2, AlertTriangle } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid } from 'recharts';
import { useSseStream } from '@/lib/useSseStream';
import { listDecisions, listDetectionRules, getDecisionSummary, type Decision } from '@/lib/api';

/**
 * Raw payload from /v1/detection/decisions/stream.
 */
interface DecisionStreamDto {
  request_id: string;
  decision: 'allow' | 'mask' | 'step_up' | 'block';
  statement_type: string;
  tables_touched: string[];
  actor_user_id: string;
  actor_role: string;
  application: string;
  severity: string;
  duration_ms: number;
  decision_explanation: string;
  occurred_at: string;
}

interface DecisionFeedItem {
  id: string;
  ts: string;
  reqId: string;
  actor: string;
  table: string;
  decision: 'allow' | 'mask' | 'step_up' | 'block';
  reason: string;
}

function toFeedItem(raw: DecisionStreamDto, sseId: string): DecisionFeedItem {
  const ts = new Date(raw.occurred_at);
  return {
    id: sseId || raw.request_id,
    ts: ts.toLocaleTimeString('en-IN', { hour12: false }),
    reqId: raw.request_id,
    actor: raw.actor_user_id,
    table: raw.tables_touched[0] ?? '-',
    decision: raw.decision,
    reason: raw.decision_explanation || '-',
  };
}

export function DetectionDashboard() {
  const { events } = useSseStream<DecisionStreamDto>({
    url: '/api/v1/detection/decisions/stream',
    bufferSize: 12,
    acceptEventName: (name) => name.startsWith('decision.'),
  });
  const feed: DecisionFeedItem[] = events.map((e) => toFeedItem(e.data, e.id));

  // Live aggregates from /v1/detection/decisions + /rules.
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [activeRulesCount, setActiveRulesCount] = useState(0);
  /** Counters over every decision (server). `decisions` is a page, for latency only. */
  const [summary, setSummary] = useState<Awaited<ReturnType<typeof getDecisionSummary>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = (signal?: AbortSignal) =>
    Promise.allSettled([listDecisions(), listDetectionRules(), getDecisionSummary()]).then(([decRes, ruleRes, sumRes]) => {
      if (signal?.aborted) return;
      if (decRes.status === 'fulfilled') setDecisions(decRes.value.decisions);
      if (sumRes.status === 'fulfilled') setSummary(sumRes.value);
      if (ruleRes.status === 'fulfilled') {
        setActiveRulesCount(
          ruleRes.value.rules.filter((r) => r.status === 'active' || r.status === 'published').length,
        );
      }
      const errs: string[] = [];
      if (decRes.status === 'rejected') errs.push(`decisions: ${(decRes.reason as Error)?.message ?? 'failed'}`);
      if (ruleRes.status === 'rejected') errs.push(`rules: ${(ruleRes.reason as Error)?.message ?? 'failed'}`);
      if (sumRes.status === 'rejected') errs.push(`decision counts: ${(sumRes.reason as Error)?.message ?? 'failed'}`);
      setError(errs.length > 0 ? errs.join(' · ') : null);
      setLoading(false);
    });

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  const cutoffHr  = Date.now() - 3600 * 1000;
  const cutoff24h = Date.now() - 24 * 3600 * 1000;
  // Counted by the server over every decision (release 2.1 item 4); the page of
  // decisions loaded here used to be counted, so a busy hour stopped at its size.
  void cutoffHr;
  void cutoff24h;
  const decisionsLastHour = summary?.lastHour ?? 0;
  const blocked24h        = summary?.blocked24h ?? 0;
  // Compute average decision latency from any decisions that report
  // durationMs. Returns null when no decisions have latency data so
  // the KPI shows "-" rather than a misleading 0.
  const avgBlockLatencyMs: number | null = (() => {
    const withLatency = decisions.filter(
      (d): d is typeof d & { durationMs: number } =>
        typeof (d as unknown as { durationMs?: number }).durationMs === 'number' &&
        d.decision === 'block',
    );
    if (withLatency.length === 0) return null;
    const sum = withLatency.reduce((acc, d) => acc + d.durationMs, 0);
    return Math.round(sum / withLatency.length);
  })();

  // Decision mix per minute over the last 60 minutes - replaces the
  // synthetic Math.sin trend that was here before.
  const decisionTrend = useMemo(
    () => (summary?.perMinute ?? Array.from({ length: 60 }, () => ({ block: 0, mask: 0, step_up: 0 })))
      .map((b, i) => ({ m: `${59 - i}m`, ...b })),
    [summary],
  );

  return (
    <PageContainer>
      <PageHeader
        title="Operator Dashboard"
        subtitle="Pre-execution prevention. Triple-Redundancy decisions. Banking 2P break-glass. Synthetic-Error Contract maps every decision to a BLK Request ID across audit, SIEM, Why-Blocked."
        badges={[
          { label: 'DDR', variant: 'glass' },
          { label: 'Triple Redundancy', variant: 'info' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <div className="flex gap-2">
            {/* Q42: the triage screen, reached from here (no sidebar row in the persona spec yet). */}
            <Button size="sm" asChild data-testid="open-incidents"><Link to="/detection/incidents">Incidents</Link></Button>
            <Button size="sm" variant="glass" asChild><Link to="/detection/decisions">View all decisions</Link></Button>
          </div>
        }
      />

      {error && !loading && (
        <ErrorState
          title="Some Detection data unavailable"
          detail={error}
          onRetry={() => { setError(null); setLoading(true); refresh(); }}
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Decisions / hour" value={decisionsLastHour} icon={Gavel} hint="from last 1h of decisions" />
        <KpiCard label="Blocked (24h)" value={blocked24h} icon={ShieldCheck} glow={blocked24h > 0 ? 'critical' : 'low'} hint="decision = block" />
        <KpiCard label="Avg block latency" value={avgBlockLatencyMs ?? 0} format="ms" icon={ActivityIcon} hint={avgBlockLatencyMs === null ? 'No block decisions with latency data yet' : 'mean across blocked decisions'} />
        <KpiCard label="Active rules" value={activeRulesCount} icon={ScrollText} hint="published rules" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Live decision ticker */}
        <GlassCard className="lg:col-span-2 p-6">
          <CardHeader className="p-0 mb-3 flex-row items-center justify-between">
            <div>
              <CardTitle>Live decision feed</CardTitle>
              <CardDescription clamp>Each row carries a BLK Request ID flowing through SQL error → audit → SIEM → Why-Blocked</CardDescription>
            </div>
            <Badge variant="glass" className="gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
              Live
            </Badge>
          </CardHeader>
          <div className="space-y-1.5">
            <AnimatePresence initial={false}>
              {feed.map((d) => (
                <motion.div
                  key={d.id}
                  layout
                  initial={{ opacity: 0, scale: 0.96, y: -8 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
                  className={`flex items-center gap-3 p-2.5 rounded-md text-xs ${
                    d.decision === 'block' ? 'glass-lite glow-critical' : 'glass-lite'
                  }`}
                >
                  <span className="font-mono text-[10px] text-muted-foreground w-16 shrink-0">{d.ts}</span>
                  <Badge
                    variant={d.decision === 'allow' ? 'success' : d.decision === 'block' ? 'critical' : d.decision === 'mask' ? 'info' : 'warning'}
                    className="text-[9px] w-14 justify-center"
                  >
                    {d.decision}
                  </Badge>
                  <code className="font-mono text-[11px] truncate flex-1">{d.actor}</code>
                  <span className="text-muted-foreground shrink-0">→</span>
                  <code className="font-mono text-[11px] truncate flex-1">{d.table}</code>
                  <span className="text-[10px] text-muted-foreground shrink-0">{d.reason}</span>
                  <code className="font-mono text-[10px] text-primary opacity-80 shrink-0">{d.reqId}</code>
                </motion.div>
              ))}
            </AnimatePresence>
            {feed.length === 0 && (
              <div className="text-center py-12 text-muted-foreground text-sm">
                Listening for decisions...
              </div>
            )}
          </div>
        </GlassCard>

        {/* Decisions by type */}
        <GlassCard className="p-6">
          <CardHeader className="p-0 mb-3">
            <CardTitle>Last hour decision mix</CardTitle>
          </CardHeader>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={decisionTrend}>
              <defs>
                <linearGradient id="d-blk" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--sev-critical))" stopOpacity={0.7} /><stop offset="100%" stopColor="hsl(var(--sev-critical))" stopOpacity={0.05} /></linearGradient>
                <linearGradient id="d-mask" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--info))" stopOpacity={0.7} /><stop offset="100%" stopColor="hsl(var(--info))" stopOpacity={0.05} /></linearGradient>
                <linearGradient id="d-step" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--warning))" stopOpacity={0.7} /><stop offset="100%" stopColor="hsl(var(--warning))" stopOpacity={0.05} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
              <XAxis dataKey="m" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} stroke="hsl(var(--border))" />
              <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} stroke="hsl(var(--border))" />
              <RTooltip contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
              <Area type="monotone" dataKey="block" stackId="1" stroke="hsl(var(--sev-critical))" fill="url(#d-blk)" strokeWidth={2} />
              <Area type="monotone" dataKey="mask" stackId="1" stroke="hsl(var(--info))" fill="url(#d-mask)" strokeWidth={2} />
              <Area type="monotone" dataKey="step_up" stackId="1" stroke="hsl(var(--warning))" fill="url(#d-step)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </GlassCard>
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <QuickLink to="/detection/decisions" icon={Gavel} label="Decisions" />
        <QuickLink to="/detection/rules" icon={ScrollText} label="Rules" />
        <QuickLink to="/detection/playbooks" icon={Workflow} label="Playbooks" />
        <QuickLink to="/detection/break-glass" icon={KeyRound} label="Break-Glass" />
        <QuickLink to="/detection/siem-gallery" icon={Grid3x3} label="SIEM Gallery" />
      </div>
    </PageContainer>
  );
}

function QuickLink({ to, icon: Icon, label }: { to: string; icon: React.ComponentType<{ className?: string }>; label: string }) {
  return (
    <Link to={to}>
      <GlassCard className="p-4 flex flex-col items-center gap-2 lift cursor-pointer">
        <Icon className="h-5 w-5 text-primary" />
        <span className="text-xs font-medium text-center">{label}</span>
      </GlassCard>
    </Link>
  );
}

// (DECISIONS_TREND synthetic Math.sin/random series previously lived
// here. Replaced with `decisionTrend` computed from real decisions in
// the Dashboard component above.)
